import { nonStandardPushErrorEvent } from "../../ServiceDesk/sdServices/non-standard-logging";

const indexedDbWrapperLogName = "indexed-db-storage-wrapper";
/**
 *  A lightweight storage wrapper for JUST WCB. We will have to deal with heartbeats and context service at a later point,
 *  but for the purpose of this prod viability test, this is just targeting WCB.
 *
 * I intend to fully rewrite this indexeddb wrapper once indexed db proves to be viable. Possibly using a 3rd party library as a wrapper,
 * and definitely using Promises instead of polling (this will just require us to rewrite a bunch of our APIs, especially how we handle
 * context shutdown, and possibly how we get heartbeats).
 */
export class IndexedDbStorageWrapper implements Storage {
  private attemptsToOpen = 0;
  private delayInMillisecondsBetweenOpenAttempts = 500;
  private db: IDBDatabase;
  private inMemoryMap: Map<string, string>;
  private inProcessOfUpdatingMemoryMap = false;
  private readyPromise: Promise<void>;
  private afterPollCallbacks: (() => any)[] = [];

  constructor() {
    this.inMemoryMap = new Map<string, string>();
    this.initializeIndexedDB();
  }

  public initializeIndexedDB() {
    this.attemptsToOpen++;
    this.readyPromise = new Promise<void>((resolve) => {
      let openRequest = window.indexedDB.open("WindowNotificationDB");
      openRequest.onsuccess = (event) => {
        this.db = openRequest.result;
        window.setInterval(this.pollStorage.bind(this), 500);
        resolve();
      };
      openRequest.onerror = (event) => {
        nonStandardPushErrorEvent(
          indexedDbWrapperLogName,
          `On attempt ${this.attemptsToOpen} An error occurred while opening indexdb on ${openRequest.error}`,
          { isUnhandled: false }
        );
        if (this.attemptsToOpen < 3) {
          window.setTimeout(this.initializeIndexedDB.bind(this), this.delayInMillisecondsBetweenOpenAttempts);
        }
      };
      openRequest.onupgradeneeded = (event) => {
        let db: IDBDatabase = openRequest.result;
        db.createObjectStore("notifications", { keyPath: "notificationId" });
      };
    });
  }

  public whenReady() {
    return this.readyPromise;
  }

  public registerAfterPollCallback(callback: () => any) {
    this.afterPollCallbacks.push(callback);
  }
  private pollStorage() {
    if (this.inProcessOfUpdatingMemoryMap) {
      return;
    }
    this.inProcessOfUpdatingMemoryMap = true;
    let newMemoryMap = new Map<string, string>();
    let request: IDBRequest = this.db
      .transaction(["notifications"], "readonly")
      .objectStore("notifications")
      .openCursor();
    request.onsuccess = (event) => {
      let cursor = (<any>event.target).result;
      if (cursor) {
        newMemoryMap.set(cursor.value.notificationId, cursor.value.value);
        cursor.continue();
      } else {
        this.inMemoryMap = newMemoryMap;
        this.inProcessOfUpdatingMemoryMap = false;
        this.afterPollCallbacks.forEach((c) => c());
      }
    };
    request.onerror = (event) => {
      nonStandardPushErrorEvent(indexedDbWrapperLogName, "An error occurred while polling storage. " + request.error, {
        isUnhandled: false
      });
      this.inProcessOfUpdatingMemoryMap = false;
    };
  }

  private checkDbReady() {
    if (!this.db) {
      let message = "Trying to call into indexedDB before it was ready";
      nonStandardPushErrorEvent(indexedDbWrapperLogName, message, { isUnhandled: false });
      throw new Error(message);
    }
  }
  [index: number]: string;
  [key: string]: any;
  length: number;
  clear(): void {
    throw new Error("Method not implemented.");
  }
  getItem(key: string): string {
    return this.inMemoryMap.get(key);
  }
  key(index: number): string {
    throw new Error("Method not implemented.");
  }
  removeItem(key: string): void {
    if (!key) {
      return;
    }
    this.checkDbReady();
    let request = this.db.transaction(["notifications"], "readwrite").objectStore("notifications").delete(key);
    request.onerror = (event) => {
      nonStandardPushErrorEvent(
        indexedDbWrapperLogName,
        "An error occurred while delete from storage " + request.error,
        { isUnhandled: false }
      );
    };
    request.onsuccess = () => {
      this.pollStorage();
    };
  }
  setItem(key: string, data: string): void {
    this.checkDbReady();
    let request = this.db
      .transaction(["notifications"], "readwrite")
      .objectStore("notifications")
      .put({ value: data, notificationId: key });
    request.onerror = (event) => {
      nonStandardPushErrorEvent(
        indexedDbWrapperLogName,
        "An error occurred while inserting to storage " + request.error,
        { isUnhandled: false }
      );
    };
    request.onsuccess = () => {
      this.pollStorage();
    };
  }

  entries(): Map<string, string> {
    return this.inMemoryMap;
  }
}
